import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardNacional from './DashboardNacional';
import DashboardExterior from './DashboardExterior';
import DashboardProventos from './DashboardProventos';
import DashboardGastos from './DashboardGastos';
import DashboardControle from './DashboardControle';
import usePageMeta from './hooks/usePageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIAS_META = [
  'Bluechips',
  'Smallcaps',
  "FII's",
  'Business',
  "REIT's",
  'Fixed Income',
  'Compra de Dólar',
  'Poupança',
  'Tesouro Direto',
  'Reserva de Valor',
  'Investimentos Gerais'
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatUSD = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
};

function DashboardGeral() {
  usePageMeta("AP - Finanças Pessoais", "ap");
  
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState(null);
  const [progressoMeta, setProgressoMeta] = useState(null);
  const [showCriarMeta, setShowCriarMeta] = useState(false);
  const [editandoMeta, setEditandoMeta] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('overview');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    carregarDados();
  }, []);

  const getAuthHeaders = () => {
    return {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    };
  };

  const carregarDados = async () => {
    try {
      const [statsRes, metaRes, progressoRes] = await Promise.all([
        axios.get(`${API}/dashboard-geral/estatisticas`, getAuthHeaders()),
        axios.get(`${API}/meta-principal`, getAuthHeaders()),
        axios.get(`${API}/meta-principal/progresso`, getAuthHeaders())
      ]);
      
      setStats(statsRes.data);
      setMeta(metaRes.data);
      setProgressoMeta(progressoRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        toast.error('Erro ao carregar dados');
      }
    }
  };

  const handleCriarMeta = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const data = {
      categoria: formData.get('categoria'),
      data_meta: formData.get('data_meta'),
      valor_meta: parseFloat(formData.get('valor_meta'))
    };

    try {
      if (editandoMeta && meta) {
        await axios.put(`${API}/meta-principal/${meta.id}`, data, getAuthHeaders());
        toast.success('Meta atualizada');
      } else {
        await axios.post(`${API}/meta-principal`, data, getAuthHeaders());
        toast.success('Meta criada');
      }
      setShowCriarMeta(false);
      setEditandoMeta(false);
      carregarDados();
    } catch (error) {
      toast.error('Erro ao salvar meta');
    }
  };

  const handleExcluirMeta = async () => {
    if (!window.confirm('Deseja excluir a meta principal?')) return;
    try {
      await axios.delete(`${API}/meta-principal/${meta.id}`, getAuthHeaders());
      toast.success('Meta excluída');
      setMeta(null);
      setProgressoMeta(null);
      carregarDados();
    } catch (error) {
      toast.error('Erro ao excluir meta');
    }
  };

  const handleEditarMeta = () => {
    setEditandoMeta(true);
    setShowCriarMeta(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/dashboard-geral')}
                className="w-16 h-16 flex items-center justify-center mr-3 hover:opacity-80 transition-opacity"
                title="Ir para Dashboard Geral"
              >
                <img src="/ap-logo.png" alt="AP Logo" className="w-full h-full object-contain" />
              </button>
              <button 
                onClick={() => {
                  // Salvar o template 'Finanças Pessoais' e navegar com reload
                  localStorage.setItem('currentTemplate', 'Finanças Pessoais');
                  window.location.href = '/dashboard';
                }}
                className="text-sm text-gray-600 font-medium hover:text-indigo-600 transition-colors cursor-pointer"
              >
                Finanças Pessoais
              </button>
            </div>

            <div className="flex items-center gap-4">
              {/* Botão Investimentos */}
              <button
                onClick={() => {
                  // Salvar flag no localStorage para abrir o modal de investimentos
                  localStorage.setItem('openInvestmentModal', 'true');
                  navigate('/dashboard');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
                data-testid="investimentos-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden md:inline font-medium">Investimentos</span>
              </button>

              {/* Botão Quero Economizar Agora com efeito pulse */}
              <button
                onClick={() => window.location.href = '/igreen'}
                className="flex flex-col items-center justify-center px-4 py-2 text-white rounded-lg transition-all shadow-md hover:shadow-lg animate-pulse-soft"
                data-testid="economizar-button"
                style={{ 
                  backgroundColor: '#22c55e',
                  minHeight: '48px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#22c55e'}
              >
                <span className="font-semibold text-sm md:text-base leading-tight text-center">
                  Quero Economizar Agora
                </span>
                <span className="text-xs opacity-90 mt-0.5">
                  conta de energia
                </span>
              </button>
            
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                data-testid="logout-button"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Desktop / Drawer Mobile */}
        <aside className={`
          fixed lg:sticky top-0 lg:top-[104px] left-0 h-screen lg:h-[calc(100vh-104px)] bg-white shadow-lg z-30
          transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          w-64 lg:w-20 xl:w-64
        `}>
          <div className="h-full overflow-y-auto p-4">
            <nav className="space-y-2">
              <button
                onClick={() => {
                  setSelectedMenu('overview');
                  setSidebarOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                  selectedMenu === 'overview'
                    ? 'bg-indigo-100 text-indigo-700 font-semibold'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>Overview</span>
              </button>

              <button
                onClick={() => {
                  setSelectedMenu('controle');
                  setSidebarOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                  selectedMenu === 'controle'
                    ? 'bg-purple-100 text-purple-700 font-semibold'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
                data-testid="menu-controle"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Controle</span>
              </button>

              <button
                onClick={() => {
                  setSelectedMenu('nacional');
                  setSidebarOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                  selectedMenu === 'nacional'
                    ? 'bg-indigo-100 text-indigo-700 font-semibold'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                </svg>
                <span>Nacional</span>
              </button>

              <button
                onClick={() => {
                  setSelectedMenu('exterior');
                  setSidebarOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                  selectedMenu === 'exterior'
                    ? 'bg-indigo-100 text-indigo-700 font-semibold'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Exterior</span>
              </button>

              <button
                onClick={() => {
                  setSelectedMenu('proventos');
                  setSidebarOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                  selectedMenu === 'proventos'
                    ? 'bg-indigo-100 text-indigo-700 font-semibold'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Proventos</span>
              </button>

              <button
                onClick={() => {
                  setSelectedMenu('gastos');
                  setSidebarOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                  selectedMenu === 'gastos'
                    ? 'bg-red-100 text-red-700 font-semibold'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
                data-testid="menu-gastos"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                </svg>
                <span>Gastos</span>
              </button>
            </nav>
          </div>
        </aside>

        {/* Overlay Mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Menu Mobile Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-28 left-4 z-40 p-2 bg-white rounded-lg shadow-lg"
        >
          <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 lg:ml-0">
          {selectedMenu === 'controle' && (
            <DashboardControle getAuthHeaders={getAuthHeaders} />
          )}

          {selectedMenu === 'nacional' && (
            <DashboardNacional getAuthHeaders={getAuthHeaders} />
          )}

          {selectedMenu === 'exterior' && (
            <DashboardExterior getAuthHeaders={getAuthHeaders} />
          )}

          {selectedMenu === 'proventos' && (
            <DashboardProventos getAuthHeaders={getAuthHeaders} />
          )}

          {selectedMenu === 'gastos' && (
            <DashboardGastos getAuthHeaders={getAuthHeaders} />
          )}
          
          {selectedMenu === 'overview' && stats && (
            <div className="max-w-7xl mx-auto">
              <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard Geral</h1>

              {/* Cards de Estatísticas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Total Investido */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Investido</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_investido)}</p>
                    </div>
                  </div>
                </div>

                {/* Patrimônio Total */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Patrimônio Total</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.patrimonio_total)}</p>
                      <p className="text-xs text-gray-500 mt-1">Investimentos + Imóveis</p>
                    </div>
                  </div>
                </div>

                {/* Total em Dólares */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-emerald-100">
                      <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total em Dólares Possuídos</p>
                      <p className="text-2xl font-bold text-gray-900">{formatUSD(stats.total_dolares)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Meta Principal */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4">META PRINCIPAL</h2>

                {!meta && !progressoMeta ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-4">Nenhuma meta criada ainda</p>
                    <button
                      onClick={() => {
                        setEditandoMeta(false);
                        setShowCriarMeta(true);
                      }}
                      className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all"
                    >
                      Criar Meta
                    </button>
                  </div>
                ) : progressoMeta && (
                  <div>
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Meta: {progressoMeta.meta.categoria}
                        </span>
                        <span className="text-sm font-bold text-indigo-600">
                          {progressoMeta.percentual}%
                        </span>
                      </div>
                      
                      {/* Barra de Progresso */}
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-indigo-600 to-purple-600 h-full transition-all duration-500 rounded-full"
                          style={{ width: `${progressoMeta.percentual}%` }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">Valor Atual</p>
                        <p className="text-lg font-bold text-gray-900">{formatCurrency(progressoMeta.valor_atual)}</p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">Valor Meta</p>
                        <p className="text-lg font-bold text-indigo-600">{formatCurrency(progressoMeta.valor_meta)}</p>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600">Falta</p>
                        <p className="text-lg font-bold text-orange-600">{formatCurrency(progressoMeta.falta)}</p>
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 mb-4">
                      <p>Data da Meta: <strong>{formatDate(progressoMeta.meta.data_meta)}</strong></p>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleEditarMeta}
                        className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                      >
                        Editar Meta
                      </button>
                      <button
                        onClick={handleExcluirMeta}
                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                      >
                        Excluir Meta
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Modal Criar/Editar Meta */}
      {showCriarMeta && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => {
          setShowCriarMeta(false);
          setEditandoMeta(false);
        }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">{editandoMeta ? 'Editar Meta' : 'Criar Meta Principal'}</h2>
                <button onClick={() => {
                  setShowCriarMeta(false);
                  setEditandoMeta(false);
                }} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleCriarMeta} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categoria de Investimento *</label>
                  <select
                    name="categoria"
                    required
                    defaultValue={editandoMeta && meta ? meta.categoria : ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Selecione...</option>
                    {CATEGORIAS_META.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data da Meta *</label>
                  <input
                    type="date"
                    name="data_meta"
                    required
                    defaultValue={editandoMeta && meta ? meta.data_meta : ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor da Meta (R$) *</label>
                  <input
                    type="number"
                    name="valor_meta"
                    step="0.01"
                    min="0"
                    required
                    defaultValue={editandoMeta && meta ? meta.valor_meta : ''}
                    placeholder="Ex: 100000.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCriarMeta(false);
                    setEditandoMeta(false);
                  }}
                  className="flex-1 px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                >
                  {editandoMeta ? 'Atualizar' : 'Criar Meta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardGeral;
